import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { WrappedFieldProps } from "redux-form";

import { FormDropdown as FormDropdownBase } from "@seneca/senekit";

type OptionBase<Value> = { value: Value };

type Props<Value, Option extends OptionBase<Value>> = WrappedFieldProps & {
  options: Option[];
  isMulti?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  label: React.ReactNode;
  placeholder: string;
  size?: "small" | "medium";
  dataNoTranslate: boolean;
  openMenuOnFocus?: boolean;
  defaultOption?: Option;
};

export function FormDropdown<T, Option extends OptionBase<T>>(
  props: Props<T, Option>
) {
  const {
    input: { name, value, onChange, onBlur },
    meta: { error, submitFailed },
    options,
    label,
    placeholder,
    size,
    isDisabled,
    isMulti,
    isSearchable,
    isClearable,
    dataNoTranslate,
    openMenuOnFocus,
    defaultOption
  } = props;

  // Addresses onBlur issue on mobile https://github.com/JedWatson/react-select/issues/2692
  const valueRef = useRef<T | T[] | undefined>(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const selectedOption = useMemo(
    () =>
      isMulti
        ? options.filter(option => value.includes(option.value))
        : options.find(option => option.value === value),
    [value, options, isMulti]
  );

  const handleChange = useCallback(
    (option: Option | Option[]) => {
      const newSelection = isMulti
        ? (option as Option[]).map(option => option.value)
        : (option as Option).value;
      valueRef.current = newSelection;
      onChange(newSelection);
    },
    [onChange, isMulti]
  );

  const handleBlur = useCallback(() => onBlur(valueRef.current), [onBlur]);

  return (
    <FormDropdownBase
      name={name}
      value={selectedOption}
      options={options}
      onChange={handleChange as any}
      onBlur={handleBlur}
      label={label}
      placeholder={placeholder}
      showError={submitFailed && !!error}
      error={error}
      size={size}
      isDisabled={isDisabled}
      isMulti={isMulti}
      isSearchable={isSearchable}
      isClearable={isClearable}
      dataNoTranslate={dataNoTranslate}
      openMenuOnFocus={openMenuOnFocus}
      defaultValue={defaultOption}
    />
  );
}
